import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, Col, Dropdown, Input, Menu, Row } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ExportButton from '../ExportButton/ExportButton';
import ImportButton from '../importButton';
import Datatable from '../DataTable/Datatable';
import PageHeaderCustom from '../PageHeader/PageHeader';
import ContentCustom from '../ContentCustom/ContentCustom';

const { Search } = Input;

const SimpleResourceLandingLayout = ({
  resourceName,
  tradKey,
  dataToFetch,
  columns,
  customActionColumn,
  headers,
  children,
  populate,
  extraQuery,
  extraHeader,
  extraButtons,
  exportUrl,
  withCreateButton,
  withUploadButton,
  forceRefresh,
  withSubRoutes,
  resourceModelName,
  scroll,
  customRoute
}) => {
  const { pathname } = useLocation();
  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [searchValue, setSearchValue] = useState(params.get('k'));

  const searchResource = (value) => {
    if (value) {
      history.push({
        pathname,
        search: `?k=${value}`
      });
    } else {
      history.push({
        pathname
      });
    }
  };

  useEffect(() => {
    setSearchValue(params.get('k'));
  }, [params]);

  const menu = (
    <Menu>
      {headers && (
        <Menu.Item>
          <ExportButton
            dataName={resourceName}
            headers={headers}
            url={`/${exportUrl || resourceName}`}
            fileName={`${resourceName}.csv`}
            populate={populate}
            extraQuery={extraQuery}
          />
        </Menu.Item>
      )}
      <Menu.Item>
        <ImportButton resourceName={resourceModelName} />
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <PageHeaderCustom
        title={tradKey || t(`${resourceName}.title`)}
        withSubRoutes={withSubRoutes}
        extra={extraHeader}
      />
      <ContentCustom>
        <Row justify="space-between" gutter={[8, 16]}>
          <Col>
            <Search
              allowClear
              placeholder={t('placeholder.search')}
              defaultValue={searchValue}
              onSearch={(value) => searchResource(value)}
            />
          </Col>
          {!withCreateButton && headers && (
            <Col>
              <Dropdown overlay={menu}>
                <Button type="link">
                  <MenuOutlined
                    style={{ fontSize: 16, color: 'var(--textColor)' }}
                  />
                </Button>
              </Dropdown>
            </Col>
          )}
          {withCreateButton && (
            <Col>
              <Row align="middle">
                {extraButtons}
                {withUploadButton && (
                  <Dropdown overlay={menu}>
                    <Button type="link">
                      <MenuOutlined
                        style={{ fontSize: 16, color: 'var(--textColor)' }}
                      />
                    </Button>
                  </Dropdown>
                )}
              </Row>
            </Col>
          )}
        </Row>
        <Row gutter={[8, 16]}>{children}</Row>
        <Datatable
          style={{ marginTop: 24 }}
          resourceName={dataToFetch || resourceName}
          customRoute={customRoute}
          searchValue={searchValue}
          columns={columns}
          path={exportUrl}
          customActionColumn={customActionColumn}
          extraQuery={extraQuery}
          populate={populate}
          forceRefresh={forceRefresh}
          scroll={scroll}
        />
      </ContentCustom>
    </>
  );
};

SimpleResourceLandingLayout.propTypes = {
  resourceName: PropTypes.string.isRequired,
  customRoute: PropTypes.string,
  tradKey: PropTypes.string,
  dataToFetch: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  customActionColumn: PropTypes.bool,
  headers: PropTypes.arrayOf(PropTypes.shape({})),
  extraQuery: PropTypes.string,
  extraHeader: PropTypes.element,
  extraButtons: PropTypes.element,
  exportUrl: PropTypes.string,
  populate: PropTypes.string,
  withCreateButton: PropTypes.bool,
  withUploadButton: PropTypes.bool,
  forceRefresh: PropTypes.bool,
  withSubRoutes: PropTypes.bool,
  resourceModelName: PropTypes.string,
  scroll: PropTypes.number,
  children: PropTypes.arrayOf(PropTypes.shape({}))
};

SimpleResourceLandingLayout.defaultProps = {
  customRoute: '',
  tradKey: null,
  headers: null,
  extraQuery: null,
  extraHeader: null,
  extraButtons: null,
  exportUrl: null,
  populate: null,
  customActionColumn: false,
  withCreateButton: true,
  withUploadButton: true,
  dataToFetch: null,
  forceRefresh: null,
  withSubRoutes: false,
  resourceModelName: null,
  scroll: null,
  children: undefined
};

export default SimpleResourceLandingLayout;
